import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES, DEMO_VIDEOS, DEMO_FRAME_COUNT } from "../mock-data";
import { useScrollFrameSequence } from "../../useScrollFrameSequence";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * VEIL — Slat & Grid Choreography
 *
 * Pure black + bone + magenta accent. Image shredding & reformation.
 *
 *  ACT I  – Hero: image shredded into 12 vertical slats, each slides
 *           in from random direction (up/down alternating). Slats
 *           settle; title fades in over.
 *  ACT II – PINNED 5×5 GRID SHATTER: image grid (25 squares) scatters
 *           to random positions then reforms into a NEW image.
 *  ACT III– FRAME-STEP MONTAGE: scroll = video.currentTime + diagonal
 *           text scrolls, accent color sweeps across.
 *  ACT IV – DIAGONAL CLIP-PATH STORY: text reveals via diagonal wipes
 *           from corners (4 different directions per paragraph).
 *  ACT V  – HORIZONTAL PINNED SCRUB: tall cards slide in from sides
 *           on horizontal pinned scroll, alternating left/right.
 *  ACT VI – RSVP with magnetic 3D cursor follow.
 */
export function VeilLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const tiltRef = useRef<HTMLDivElement>(null);
  const vidSectionRef = useRef<HTMLElement | null>(null);
  const vidCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Act III frame-step montage — Apple-style frame sequence.
  useScrollFrameSequence({
    containerRef: vidSectionRef,
    canvasRef: vidCanvasRef,
    source: { kind: "video", url: DEMO_VIDEOS.timelineScroll, count: DEMO_FRAME_COUNT },
    pinViewports: 3.5,
  });

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate().toString().padStart(2, "0") ?? "--";
  const monthShort = d?.toLocaleString("en-US", { month: "short" }).toUpperCase() ?? "—";
  const year = d?.getFullYear() ?? 2026;

  const SLAT_COUNT = 12;
  const GRID_SIZE = 5; // 5×5

  useEffect(() => {
    const card = tiltRef.current;
    if (!card) return;
    const handleMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      card.style.transform = `perspective(1200px) rotateY(${dx * 10}deg) rotateX(${-dy * 10}deg)`;
    };
    const reset = () => { card.style.transform = "perspective(1200px) rotateY(0) rotateX(0)"; };
    window.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", reset);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", reset);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.4, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── ACT I: SLAT SHRED HERO ─────────────────────────────────────
      // Slats slide in from alternating directions
      gsap.from(".vl-slat", {
        y: (i) => (i % 2 === 0 ? "-100%" : "100%"),
        opacity: 0,
        stagger: { each: 0.05, from: "random" },
        duration: 1.4,
        ease: "expo.out",
        delay: 0.2,
      });

      // Subtle inhale-exhale on slats (vertical breathing after settle)
      gsap.to(".vl-slat", {
        y: (i) => (i % 2 === 0 ? -4 : 4),
        duration: 2.6,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        stagger: 0.08,
        delay: 2,
      });

      // Title fades in over slats
      const heroSplit = new SplitText(".vl-hero-title", { type: "chars" });
      gsap.from(heroSplit.chars, {
        opacity: 0,
        y: 30,
        rotateZ: 5,
        stagger: 0.04,
        duration: 1.2,
        ease: "expo.out",
        delay: 1.4,
      });

      gsap.from(".vl-hero-meta", { opacity: 0, y: 20, stagger: 0.1, duration: 1, ease: "power3.out", delay: 1.8 });
      gsap.from(".vl-hero-accent", { scaleX: 0, transformOrigin: "center", duration: 1.4, ease: "expo.out", delay: 2 });

      // Marquee tickers
      gsap.from(".vl-marquee-l", { x: "100%", duration: 1.6, ease: "expo.out", delay: 0.3 });
      gsap.from(".vl-marquee-r", { x: "-100%", duration: 1.6, ease: "expo.out", delay: 0.5 });

      // ── ACT II: PINNED 5×5 GRID SCATTER ────────────────────────────
      const gridSection = document.querySelector<HTMLElement>(".vl-grid-section");
      const tiles = document.querySelectorAll<HTMLElement>(".vl-tile");

      if (gridSection && tiles.length > 0) {
        // Generate random offsets per tile
        const offsets = Array.from(tiles).map(() => ({
          x: (Math.random() - 0.5) * 400,
          y: (Math.random() - 0.5) * 400,
          rotate: (Math.random() - 0.5) * 60,
          scale: 0.6 + Math.random() * 0.4,
        }));

        const gridTl = gsap.timeline({
          scrollTrigger: {
            trigger: gridSection,
            start: "top top",
            end: "+=400%",
            pin: true,
            scrub: 1,
          },
        });

        // Phase 1: Image holds intact (0-25%)
        // Phase 2: Tiles SCATTER outward (25-50%)
        tiles.forEach((tile, i) => {
          gridTl.to(tile, {
            x: offsets[i].x,
            y: offsets[i].y,
            rotate: offsets[i].rotate,
            scale: offsets[i].scale,
            ease: "power2.inOut",
          }, 0.25);
        });

        // Phase 3: Tiles SWAP image source (mid-scatter, 50%)
        tiles.forEach((tile) => {
          gridTl.to(tile, {
            "--tile-bg-opacity": 0,
            duration: 0.1,
            onComplete() { tile.style.setProperty("--tile-img", `url(${galleryImages[2]})`); },
          }, 0.5);
          gridTl.to(tile, { "--tile-bg-opacity": 1, duration: 0.1 }, 0.55);
        });

        // Phase 4: Tiles REFORM into new image (50-75%)
        tiles.forEach((tile) => {
          gridTl.to(tile, {
            x: 0, y: 0, rotate: 0, scale: 1,
            ease: "expo.out",
          }, 0.6);
        });

        // Phase 5: Final caption fades in (75-100%)
        const caption = document.querySelector(".vl-grid-caption");
        if (caption) {
          gridTl.from(caption, { opacity: 0, y: 30, duration: 0.5, ease: "power3.out" }, 0.85);
        }
      }

      // ── ACT III: FRAME-STEP MONTAGE — pin + canvas handled by hook ──
      const vidSection = document.querySelector<HTMLElement>(".vl-video-section");
      if (vidSection) {
        gsap.fromTo(".vl-diag-1", { x: "-100%", y: "-50%" }, {
          x: "100%", y: "50%", ease: "none",
          scrollTrigger: { trigger: vidSection, start: "top top", end: "+=350%", scrub: 1 },
        });
        gsap.fromTo(".vl-diag-2", { x: "100%", y: "50%" }, {
          x: "-100%", y: "-50%", ease: "none",
          scrollTrigger: { trigger: vidSection, start: "top top", end: "+=350%", scrub: 1 },
        });

        gsap.fromTo(".vl-color-sweep", { x: "-100%" }, {
          x: "100%", ease: "none",
          scrollTrigger: { trigger: vidSection, start: "top top", end: "+=350%", scrub: 1.2 },
        });
      }

      // ── ACT IV: DIAGONAL CLIP-PATH STORY ──────────────────────────
      // 4 paragraphs revealing from 4 different corners
      const corners = [
        "polygon(0 0, 0 0, 0 0, 0 0)",                              // top-left collapsed
        "polygon(100% 0, 100% 0, 100% 0, 100% 0)",                  // top-right
        "polygon(0 100%, 0 100%, 0 100%, 0 100%)",                  // bottom-left
        "polygon(100% 100%, 100% 100%, 100% 100%, 100% 100%)",      // bottom-right
      ];
      document.querySelectorAll(".vl-story-block").forEach((block, i) => {
        gsap.from(block, {
          clipPath: corners[i % 4],
          duration: 1.4,
          ease: "expo.inOut",
          scrollTrigger: { trigger: block, start: "top 80%", toggleActions: "play reverse play reverse" },
        });
        gsap.from(block.querySelector(".vl-story-text"), {
          opacity: 0,
          y: 20,
          duration: 1,
          delay: 0.3,
          ease: "power3.out",
          scrollTrigger: { trigger: block, start: "top 80%", toggleActions: "play reverse play reverse" },
        });
      });

      // ── ACT V: HORIZONTAL PINNED CARDS ─────────────────────────────
      const horizSection = document.querySelector<HTMLElement>(".vl-horiz-section");
      const horizInner = document.querySelector<HTMLElement>(".vl-horiz-inner");
      if (horizSection && horizInner) {
        const distance = horizInner.scrollWidth - window.innerWidth;
        const vlHorizTween = gsap.to(horizInner, {
          x: -distance,
          ease: "none",
          scrollTrigger: {
            trigger: horizSection,
            start: "top top",
            end: `+=${distance}px`,
            pin: true,
            scrub: 1,
          },
        });

        // Per-card y-bobbing entry — needs containerAnimation since cards
        // are inside a transformed (horizontally scrolling) container
        document.querySelectorAll(".vl-h-card").forEach((card, i) => {
          gsap.from(card, {
            y: i % 2 === 0 ? "-30%" : "30%",
            opacity: 0,
            duration: 1.2,
            ease: "expo.out",
            scrollTrigger: {
              trigger: card,
              containerAnimation: vlHorizTween,
              start: "left 90%",
              end: "right 10%",
              toggleActions: "play reverse play reverse",
            },
          });
        });
      }

      // ── DATE — counters + diagonal accent ──────────────────────────
      const dayEl = document.querySelector<HTMLElement>(".vl-num-day");
      const monthEl = document.querySelector<HTMLElement>(".vl-num-month");
      if (dayEl && monthEl && d) {
        const obj = { d: 0, m: 0 };
        gsap.to(obj, {
          d: d.getDate(), m: d.getMonth() + 1,
          duration: 1.4, ease: "power3.out",
          scrollTrigger: { trigger: ".vl-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
          onUpdate() {
            dayEl.textContent = Math.round(obj.d).toString().padStart(2, "0");
            monthEl.textContent = Math.round(obj.m).toString().padStart(2, "0");
          },
        });
      }
      gsap.from(".vl-date-bar", {
        scaleX: 0,
        transformOrigin: (i) => i === 0 ? "left" : "right",
        stagger: 0.15,
        duration: 1.4,
        ease: "expo.inOut",
        scrollTrigger: { trigger: ".vl-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });

      // Form
      gsap.from(".vl-form-card", {
        opacity: 0,
        y: 60,
        rotateX: -15,
        duration: 1.4,
        ease: "expo.out",
        scrollTrigger: { trigger: ".vl-form-card", start: "top 80%", toggleActions: "play reverse play reverse" },
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  const stories = [
    { dir: "Top-Left", title: "İlk Buluşma", text: "Bir bahar akşamı, beş saat hiç fark etmeden geçti. Yağmur başladı, hâlâ konuşuyorduk.", year: "Bahar 2024", place: "İSTANBUL · BEYOĞLU", img: galleryImages[0], chapter: "I" },
    { dir: "Top-Right", title: "Yolculuk", text: "Üç şehir, sonsuz hatıra. Her sokak bir hikâye, her köşede yeni bir başlangıç.", year: "Yaz 2024", place: "ROMA · PARIS · İSTANBUL", img: galleryImages[1], chapter: "II" },
    { dir: "Bottom-Left", title: "Karar Anı", text: "Plajda yıldızlar yandı. Bir tek kelime, bir ömür için söz oldu.", year: "Mayıs 2025", place: "ÇEŞME", img: galleryImages[3], chapter: "III" },
    { dir: "Bottom-Right", title: "Bugün", text: "Sizi düğünümüze davet etmekten onur duyuyoruz. Bu hikâyenin altıncı bölümünü birlikte yazalım.", year: year.toString(), place: (location ?? "").split(",")[0].toUpperCase(), img: galleryImages[4], chapter: "IV" },
  ];

  const horizCards = [
    { title: "Ceremony", time: "18:00", img: galleryImages[0], note: "Welcome champagne" },
    { title: "Cocktail", time: "20:00", img: galleryImages[1], note: "Live jazz" },
    { title: "Dinner", time: "21:00", img: galleryImages[2], note: "7 courses" },
    { title: "Dance", time: "23:00", img: galleryImages[3], note: "Until dawn" },
    { title: "After", time: "02:00", img: galleryImages[4], note: "DJ set" },
  ];

  return (
    <main className="relative overflow-x-hidden text-white" style={{ background: "#0a0a0a", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @keyframes vlMQ_L { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes vlMQ_R { from { transform: translateX(-50%); } to { transform: translateX(0); } }
        .vl-tile {
          background-image: var(--tile-img);
          background-size: 500% 500%;
          opacity: var(--tile-bg-opacity, 1);
        }
      `}</style>

      {/* ═══ ACT I: HERO with SLAT SHRED ═══════════════════════════════ */}
      <section className="relative min-h-screen overflow-hidden flex flex-col">
        {/* Image shredded into vertical slats */}
        <div className="absolute inset-0 flex">
          {Array.from({ length: SLAT_COUNT }).map((_, i) => (
            <div
              key={i}
              className="vl-slat relative h-full"
              style={{
                width: `${100 / SLAT_COUNT}%`,
                background: `url(${galleryImages[0]}) center/cover`,
                backgroundPosition: `${(i / (SLAT_COUNT - 1)) * 100}% center`,
                backgroundSize: `${SLAT_COUNT * 100}% 100%`,
                filter: "grayscale(1) contrast(1.1) brightness(0.6)",
              }}
            >
              {/* Subtle vertical line between slats */}
              {i > 0 && <div className="absolute top-0 left-0 w-px h-full bg-white/[0.03]" />}
            </div>
          ))}
        </div>

        {/* Dark vignette */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.85))" }} />

        {/* Top marquee */}
        <div className="vl-marquee-l relative h-10 border-b border-white/10 overflow-hidden flex items-center z-10">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase text-white/60" style={{ animation: "vlMQ_L 32s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">{title.toUpperCase()} <span style={{ color: "#e91e63" }}>●</span> THE VEIL <span style={{ color: "#e91e63" }}>●</span> {year} <span style={{ color: "#e91e63" }}>●</span></span>
            ))}
          </div>
        </div>

        {/* Center title */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
          <p className="vl-hero-meta text-[10px] uppercase tracking-[0.6em] text-white/40 mb-8">An Invitation · 6 Acts</p>

          <h1 className="vl-hero-title text-center font-light leading-[0.85] tracking-[-0.04em]" style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: "clamp(4rem, 14vw, 13rem)",
          }}>
            {title}
          </h1>

          <div className="vl-hero-accent mt-12 w-32 h-px" style={{ background: "#e91e63", boxShadow: "0 0 20px #e91e63" }} />

          <p className="vl-hero-meta mt-8 text-xs uppercase tracking-[0.5em] text-white/60">
            {monthShort} <span style={{ color: "#e91e63" }}>·</span> {day} <span style={{ color: "#e91e63" }}>·</span> {year}
          </p>
          <p className="vl-hero-meta mt-3 italic text-white/40" style={{ fontFamily: "'Playfair Display', serif", fontSize: 18 }}>
            {location?.split(",")[0] ?? "—"}
          </p>
        </div>

        {/* Bottom marquee */}
        <div className="vl-marquee-r relative h-10 border-t border-white/10 overflow-hidden flex items-center z-10">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase" style={{ color: "#e91e63", animation: "vlMQ_R 38s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">◇ SCROLL TO BEGIN ◇ ACT I — VI ◇ {(location ?? "").toUpperCase()} ◇</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT II: PINNED 5×5 GRID SCATTER ═══════════════════════════ */}
      <section className="vl-grid-section relative h-screen overflow-hidden flex items-center justify-center" ref={gridContainerRef}>
        <div className="absolute top-12 left-12 z-30 text-[10px] uppercase tracking-[0.5em] text-white/40">II · THE SHATTER</div>
        <div className="absolute top-12 right-12 z-30 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#e91e63" }}>● SCROLL ●</div>

        {/* Grid container */}
        <div className="relative aspect-square w-[min(80vw,80vh)]" style={{ perspective: 1200 }}>
          {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, i) => {
            const row = Math.floor(i / GRID_SIZE);
            const col = i % GRID_SIZE;
            const tileSize = 100 / GRID_SIZE;
            return (
              <div
                key={i}
                className="vl-tile absolute"
                style={{
                  width: `${tileSize}%`,
                  height: `${tileSize}%`,
                  left: `${col * tileSize}%`,
                  top: `${row * tileSize}%`,
                  ["--tile-img" as any]: `url(${galleryImages[0]})`,
                  backgroundPosition: `${(col / (GRID_SIZE - 1)) * 100}% ${(row / (GRID_SIZE - 1)) * 100}%`,
                  filter: "grayscale(1) contrast(1.1)",
                  border: "1px solid rgba(255,255,255,0.05)",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.4)",
                }}
              />
            );
          })}
        </div>

        {/* Side metadata column — visible from start */}
        <div className="absolute left-8 top-1/2 -translate-y-1/2 hidden lg:block pointer-events-none">
          <div className="space-y-6 max-w-[180px]">
            <div>
              <p className="text-[9px] uppercase tracking-[0.4em] text-white/40 mb-1">Format</p>
              <p className="text-sm italic text-white/85" style={{ fontFamily: "'Playfair Display', serif" }}>5×5 grid · 25 pieces</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.4em] text-white/40 mb-1">Method</p>
              <p className="text-sm italic text-white/85" style={{ fontFamily: "'Playfair Display', serif" }}>Scatter · re-form</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.4em] text-white/40 mb-1">Frames</p>
              <p className="text-sm italic text-white/85" style={{ fontFamily: "'Playfair Display', serif" }}>Two memories, one whole</p>
            </div>
          </div>
        </div>

        <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:block pointer-events-none text-right">
          <div className="space-y-6 max-w-[180px] ml-auto">
            <div>
              <p className="text-[9px] uppercase tracking-[0.4em] text-white/40 mb-1">Catalog</p>
              <p className="text-sm italic text-white/85 font-mono" style={{ fontSize: 13 }}>VL-{year}-002</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.4em] text-white/40 mb-1">Source</p>
              <p className="text-sm italic text-white/85" style={{ fontFamily: "'Playfair Display', serif" }}>From memory · to vow</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.4em] text-white/40 mb-1">Caption</p>
              <p className="text-sm italic text-white/85" style={{ fontFamily: "'Playfair Display', serif" }}>"İki an, tek hatıra"</p>
            </div>
          </div>
        </div>

        {/* Caption appears at the end */}
        <div className="vl-grid-caption absolute bottom-16 left-1/2 -translate-x-1/2 text-center pointer-events-none">
          <p className="text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#e91e63" }}>From fragments,</p>
          <p className="italic font-light text-2xl sm:text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>a single story.</p>
          <p className="text-[10px] uppercase tracking-[0.5em] mt-3 text-white/40">25 pieces · 1 image · ∞ meaning</p>
        </div>
      </section>

      {/* ═══ ACT III: FRAME-STEP MONTAGE ═══════════════════════════════ */}
      <section
        ref={vidSectionRef as React.RefObject<HTMLElement>}
        className="vl-video-section relative h-screen overflow-hidden"
      >
        <canvas
          ref={vidCanvasRef}
          className="vl-video-canvas absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />

        {/* Color sweep */}
        <div className="vl-color-sweep absolute top-0 left-0 w-1/3 h-full pointer-events-none" style={{
          background: "linear-gradient(90deg, transparent, rgba(233, 30, 99, 0.25), transparent)",
          filter: "blur(40px)",
        }} />

        {/* Diagonal text crawls */}
        <div className="vl-diag-1 absolute top-1/3 left-0 right-0 whitespace-nowrap pointer-events-none" style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "clamp(3rem, 9vw, 9rem)",
          fontWeight: 300,
          color: "rgba(255,255,255,0.85)",
          letterSpacing: "-0.04em",
          fontStyle: "italic",
        }}>
          {Array(4).fill(`${title.toUpperCase()} ·  `).join("")}
        </div>
        <div className="vl-diag-2 absolute top-2/3 left-0 right-0 whitespace-nowrap pointer-events-none text-right" style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "clamp(3rem, 9vw, 9rem)",
          fontWeight: 300,
          color: "#e91e63",
          letterSpacing: "-0.04em",
        }}>
          {Array(4).fill(`${monthShort} ${day} ${year} ·  `).join("")}
        </div>

        {/* Stage indicator */}
        <div className="absolute top-12 left-12 z-30 text-[10px] uppercase tracking-[0.5em] text-white/40">III · MONTAGE</div>
        <div className="absolute top-12 right-12 z-30 text-right">
          <p className="text-[10px] uppercase tracking-[0.5em]" style={{ color: "#e91e63" }}>● REC</p>
          <p className="text-[9px] uppercase tracking-[0.4em] text-white/40 mt-1 font-mono">02:00 · MONTAGE</p>
        </div>

        {/* Bottom timecode + meta strip */}
        <div className="absolute bottom-12 left-12 right-12 z-30 flex justify-between items-end pointer-events-none">
          <div>
            <p className="text-[9px] uppercase tracking-[0.4em] text-white/40 font-mono">FRAME 0001 / 0480</p>
            <p className="text-[10px] uppercase tracking-[0.5em] mt-2" style={{ color: "#e91e63" }}>A montage of moments</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.5em] text-white/40">— Scroll to advance —</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] uppercase tracking-[0.4em] text-white/40 font-mono">{monthShort} {day} · {year}</p>
            <p className="text-[10px] uppercase tracking-[0.5em] mt-2 italic" style={{ color: "#e91e63", fontFamily: "'Playfair Display', serif" }}>Veil · Act III</p>
          </div>
        </div>
      </section>

      {/* ═══ BRIDGE: TIMELINE STRIP between III and IV ════════════════ */}
      <section className="relative py-16 px-6 border-t border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-8" style={{ color: "#e91e63" }}>— Timeline —</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[
              { y: "2023", e: "Tanışma" },
              { y: "2024", e: "Yolculuk" },
              { y: "2025", e: "Söz" },
              { y: "2025", e: "Hazırlık" },
              { y: year.toString(), e: "Düğün", accent: true },
            ].map((t, i) => (
              <div key={i} className="text-center relative">
                {i < 4 && <div className="absolute top-3 left-1/2 w-full h-px hidden sm:block" style={{ background: "rgba(233, 30, 99, 0.3)" }} />}
                <div className="relative w-6 h-6 mx-auto rounded-full flex items-center justify-center mb-3" style={{
                  background: t.accent ? "#e91e63" : "transparent",
                  border: `1px solid ${t.accent ? "#e91e63" : "rgba(233,30,99,0.5)"}`,
                }}>
                  {t.accent && <span className="w-2 h-2 rounded-full bg-white" />}
                </div>
                <p className="font-light tabular-nums" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: t.accent ? "#e91e63" : "white" }}>{t.y}</p>
                <p className="text-[9px] uppercase tracking-[0.4em] text-white/50 mt-1">{t.e}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT IV: 4-CORNER CLIP REVEAL STORIES ══════════════════════ */}
      <section className="relative py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-4" style={{ color: "#e91e63" }}>IV · FOUR CHAPTERS</p>
          <h2 className="text-center font-light italic leading-tight mb-24" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
            From every corner,<br />a memory.
          </h2>

          <div className="grid sm:grid-cols-2 gap-8">
            {stories.map((s, i) => (
              <div
                key={i}
                className="vl-story-block relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  backdropFilter: "blur(10px)",
                }}
              >
                {/* Image strip */}
                <div className="relative h-56 sm:h-64 overflow-hidden">
                  <img src={s.img} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.1)" }} />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.8))" }} />
                  {/* Top tag */}
                  <div className="absolute top-3 left-3 px-2 py-1" style={{ background: "rgba(233, 30, 99, 0.85)" }}>
                    <span className="text-[10px] uppercase tracking-[0.4em] font-bold text-white">CH. {s.chapter}</span>
                  </div>
                  {/* Bottom-right meta */}
                  <div className="absolute bottom-3 right-3 text-right">
                    <p className="text-[9px] uppercase tracking-[0.3em] text-white/60">{s.place}</p>
                    <p className="text-[10px] uppercase tracking-[0.4em] mt-1" style={{ color: "#e91e63" }}>{s.year}</p>
                  </div>
                </div>

                {/* Text */}
                <div className="vl-story-text p-8 sm:p-10">
                  <p className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: "#e91e63" }}>{s.dir}</p>
                  <h3 className="font-light italic mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.5rem, 2.5vw, 2.25rem)" }}>
                    {s.title}
                  </h3>
                  <p className="text-base text-white/75 leading-relaxed italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                    "{s.text}"
                  </p>
                  <div className="w-12 h-px mt-6" style={{ background: "#e91e63" }} />
                </div>

                {/* Corner accent based on direction */}
                {i === 0 && <div className="absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 z-10" style={{ borderColor: "#e91e63" }} />}
                {i === 1 && <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 z-10" style={{ borderColor: "#e91e63" }} />}
                {i === 2 && <div className="absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 z-10" style={{ borderColor: "#e91e63" }} />}
                {i === 3 && <div className="absolute bottom-3 right-3 w-8 h-8 border-b-2 border-r-2 z-10" style={{ borderColor: "#e91e63" }} />}
              </div>
            ))}
          </div>

          {/* Stats row — by the numbers */}
          <div className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { n: "2", label: "Sevdalı" },
              { n: "3", label: "Şehir" },
              { n: "365×2", label: "Gün" },
              { n: "1", label: "Yemin" },
            ].map((s, i) => (
              <div key={i} className="text-center p-6" style={{ borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.1)" : undefined }}>
                <p className="font-light tabular-nums" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 4vw, 3.5rem)", color: "#e91e63", lineHeight: 1 }}>
                  {s.n}
                </p>
                <p className="text-[10px] uppercase tracking-[0.5em] text-white/50 mt-3">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT V: HORIZONTAL PINNED CARDS ═════════════════════════════ */}
      <section className="vl-horiz-section relative h-screen overflow-hidden">
        <div className="absolute top-12 left-12 z-30 text-[10px] uppercase tracking-[0.5em] text-white/40">V · PROGRAMME</div>
        <div className="absolute top-12 right-12 z-30 text-[10px] uppercase tracking-[0.5em]" style={{ color: "#e91e63" }}>5 ACTS</div>

        <div className="vl-horiz-inner absolute top-0 left-0 h-full flex items-center" style={{ width: "max-content" }}>
          {/* Intro panel */}
          <div className="flex-shrink-0 w-screen flex items-center justify-center px-16">
            <div className="max-w-md">
              <p className="text-[10px] uppercase tracking-[0.6em] mb-6" style={{ color: "#e91e63" }}>— Programme —</p>
              <p className="font-light italic leading-tight" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                Akşamımızı<br />beş bölümde<br />paylaşıyoruz.
              </p>
            </div>
          </div>

          {horizCards.map((c, i) => (
            <div key={i} className="vl-h-card flex-shrink-0 h-full flex" style={{ width: "80vw" }}>
              <div className="w-3/5 h-full relative overflow-hidden">
                <img src={c.img} alt="" className="w-full h-full object-cover" style={{ filter: "grayscale(1) contrast(1.05)" }} />
                <div className="absolute top-6 left-6 text-[10px] uppercase tracking-[0.4em]" style={{ color: "#e91e63" }}>Act {["I","II","III","IV","V"][i]}</div>
              </div>
              <div className="w-2/5 h-full flex flex-col justify-center px-8 sm:px-14 bg-black">
                <p className="text-[10px] uppercase tracking-[0.5em] text-white/40 mb-3">{c.note}</p>
                <p className="font-light tabular-nums mb-4" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 6vw, 5rem)", lineHeight: 1, color: "#e91e63" }}>
                  {c.time}
                </p>
                <p className="font-light italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.5rem, 2.5vw, 2.25rem)" }}>{c.title}</p>
                <div className="w-12 h-px mt-6" style={{ background: "#e91e63" }} />
              </div>
            </div>
          ))}

          {/* Outro — denser closing */}
          <div className="flex-shrink-0 w-screen flex items-center justify-center px-16 relative">
            <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px" style={{ background: "linear-gradient(180deg, transparent, rgba(233, 30, 99, 0.3), transparent)" }} />
            <div className="text-center max-w-xl">
              <p className="text-[10px] uppercase tracking-[0.6em] mb-6" style={{ color: "#e91e63" }}>● End of Programme ●</p>
              <h3 className="font-light leading-none tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 7vw, 5rem)" }}>
                — Fin —
              </h3>
              <div className="mx-auto w-16 h-px mb-6" style={{ background: "#e91e63" }} />
              <p className="italic font-light leading-snug mb-4" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.25rem, 2vw, 1.75rem)" }}>
                Akşamın 5 perdesi sona erdi.
              </p>
              <p className="text-sm text-white/60 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                Sıradaki bölüm sizinle. Aşağı kaydırın ↓
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ DATE PLATE ════════════════════════════════════════════════ */}
      <section className="vl-date-section relative min-h-[80vh] flex items-center justify-center px-6 py-32">
        <div className="max-w-5xl w-full">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-12" style={{ color: "#e91e63" }}>THE DATE</p>

          <div className="flex items-center justify-center gap-4 sm:gap-12">
            <div className="vl-date-bar h-px flex-1" style={{ background: "linear-gradient(90deg, transparent, #e91e63)" }} />
            <p className="vl-num-day font-light tabular-nums leading-none tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 14vw, 12rem)" }}>00</p>
            <div className="font-light text-white/30 leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 9vw, 8rem)" }}>·</div>
            <p className="vl-num-month font-light tabular-nums leading-none tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 14vw, 12rem)" }}>00</p>
            <div className="vl-date-bar h-px flex-1" style={{ background: "linear-gradient(-90deg, transparent, #e91e63)" }} />
          </div>

          <p className="text-center mt-6 text-2xl tabular-nums tracking-widest text-white/70" style={{ fontFamily: "'Playfair Display', serif" }}>{year}</p>
          <p className="text-center mt-6 text-xs uppercase tracking-[0.5em]" style={{ color: "#e91e63" }}>
            ● {d?.toLocaleString("en-US", { weekday: "long" }).toUpperCase()} · {d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })} ●
          </p>
        </div>
      </section>

      {/* ═══ ACT VI: RSVP magnetic tilt ════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32" style={{ perspective: 1200 }}>
        <div
          ref={tiltRef}
          className="vl-form-card w-full max-w-md p-12 relative transition-transform duration-300 ease-out"
          style={{
            background: "linear-gradient(135deg, rgba(233, 30, 99, 0.08) 0%, rgba(233, 30, 99, 0.02) 100%)",
            border: "1px solid rgba(233, 30, 99, 0.3)",
            boxShadow: "inset 0 1px 0 rgba(233, 30, 99, 0.2), 0 40px 100px rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            transformStyle: "preserve-3d",
          }}
        >
          <div className="absolute top-3 left-3 w-5 h-5 border-t border-l" style={{ borderColor: "#e91e63" }} />
          <div className="absolute top-3 right-3 w-5 h-5 border-t border-r" style={{ borderColor: "#e91e63" }} />
          <div className="absolute bottom-3 left-3 w-5 h-5 border-b border-l" style={{ borderColor: "#e91e63" }} />
          <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r" style={{ borderColor: "#e91e63" }} />

          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#e91e63" }}>VI · FINAL ACT</p>
          <h3 className="text-center font-light italic mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            Your Reply
          </h3>
          <div className="mx-auto w-12 h-px mb-10" style={{ background: "#e91e63" }} />
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
          <p className="text-center text-[10px] tracking-[0.4em] uppercase text-white/30 mt-8">
            ↑ Hover to tilt ↑
          </p>
        </div>
      </section>
    </main>
  );
}
